/**
 * 도감(codex) 도메인 부트스트래퍼 — 뷰 생성, 탭 등록, 정보 팝업/강화 콜백 결선.
 * GameScene은 도감 상세 표시와 강화 진입점을 이 부트스트래퍼를 통해서만 호출한다.
 * 이 파일이 도메인 슬라이스의 전부 — GameScene은 codex를 모른다.
 */
import { CodexTabView } from "../ui/CodexTabView.js";
import {
  SYNERGY_CODEX_UNLOCK_CHAPTER,
  buildCodexEntries,
  buildSpaceshipLauncherCodexEntries,
  buildSynergyCodexRows,
} from "../data/codex.js";
import { openCodexModuleInfo } from "../ui/codexInfoModal.js";
import { openCodexSynergyInfo } from "../ui/codexSynergyInfoModal.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";
import { SPACESHIP_DEFS } from "../data/spaceshipData.js";
import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import { t } from "../i18n/i18n.js";
import type { DomainBootstrapper } from "./BootTypes.js";
import { EconomyController } from "../systems/EconomyController.js";
import { BigNumber } from "@yjsgames/bignumber";
import { resolvePurchase } from "../systems/multiBuy.js";

export const codexBoot: DomainBootstrapper = {
  key: "codex",
  install(boot) {
    boot.tabs.registerLazy("codex", {
      mount: () => {
        const { tabContent } = boot.layout;
        // 도감 행 클릭 → 정의 기준 모듈 정보 팝업(미발견은 수치 가림). battleBoot의 infoDeps 패턴과 동형.
        const infoDeps = {
          modals: boot.modals,
          ctx: boot.ctx,
          screen: () => boot.screen(),
          discovered: () => boot.state.discovered(),
          // 도감에서도 강화석으로 레벨업 (명세: 보유 파이프/도감/파이프 상세에 강화 진입점).
          upgradeModule: (moduleId: string) => boot.commands.grid.upgradeModule(moduleId),
        };
        const view = new CodexTabView(
          boot.scene,
          tabContent.x,
          tabContent.y,
          tabContent.width,
          boot.layout.shell.tabPanelBottom,
          {
            onRowClick: (id) => openCodexModuleInfo(infoDeps, id),
            // 잠긴 셀(모든 조각 미해금) 클릭 → 흔들기(뷰) + 해금 챕터 토스트.
            onLockedClick: (chapter) => boot.ui.showToast(t("codex.lockedToast", { chapter })),
            onSynergyUpgrade: (id) =>
              boot.commands.grid.upgradeSynergy(id, view.currentSynergyMultiplier()),
            onSynergyInfoClick: (id) =>
              openCodexSynergyInfo(
                {
                  modals: boot.modals,
                  screen: () => boot.screen(),
                  levelOf: (synergyId) => boot.ctx.synergyLevels().level(synergyId),
                  openModuleInfo: (definitionId) => openCodexModuleInfo(infoDeps, definitionId),
                },
                id,
              ),
            onSynergyMultiplierChange: () => refresh(),
            onSynergyLockedClick: (chapter) => boot.ui.showToast(t("synergyCodex.lockedToast", { chapter })),
          },
        );
        const refresh = (): void => {
          const synergyPurchasePreview = (_id: string, level: number) => {
            const { count, totalCost } = resolvePurchase(
              (lv) => BigNumber.fromNumber(EconomyController.upgradeStoneCost(lv)),
              level,
              Number.POSITIVE_INFINITY,
              BigNumber.fromNumber(boot.ctx.upgradeInv().universalUpgradeStone),
              view.currentSynergyMultiplier(),
            );
            return {
              count,
              stoneCost: count > 0 ? totalCost.toNumber() : EconomyController.upgradeStoneCost(level),
            };
          };
          view.update(
            [
              ...buildCodexEntries(
                ALL_MODULE_DEFINITIONS,
                boot.state.discovered(),
                boot.ctx.inventory().allInstances(),
                (id) => boot.ctx.playerModuleLevels().level(id),
                boot.ctx.chapter().bestChapter,
                boot.state.codexSeenChapter(), // 새 해금 셀 레드닷 판정(seen 이후 해금 조각)
              ),
              ...buildSpaceshipLauncherCodexEntries(
                SPACESHIP_DEFS,
                boot.ctx.spaceship().ownedIds(),
                boot.ctx.chapter().bestChapter,
              ),
            ],
            buildSynergyCodexRows(
              (id) => boot.ctx.synergyLevels().level(id),
              (level) => EconomyController.upgradeStoneCost(level),
              boot.ctx.upgradeInv().universalUpgradeStone,
              synergyPurchasePreview,
            ),
            boot.ctx.chapter().bestChapter >= SYNERGY_CODEX_UNLOCK_CHAPTER,
            SYNERGY_CODEX_UNLOCK_CHAPTER,
          );
        };
        // 시그니처(가벼운 입력 요약) — 동일하면 refresh가 update를 스킵 → buildCodexEntries + 80카드
        // renderCards(setText/getWrappedText/drawPool) 미실행. 입력: 발견 집합 + per-def 보유개수(discovered
        // = has||ownedCount>0 에 영향) + bestChapter(조각 해금 배지). maxLevel은 도감 미표기라 제외.
        const codexSignature = (): string => {
          const counts = new Map<string, number>();
          for (const inst of boot.ctx.inventory().allInstances())
            counts.set(inst.definitionId, (counts.get(inst.definitionId) ?? 0) + 1);
          const countSig = [...counts.entries()]
            .sort()
            .map(([k, v]) => `${k}:${v}`)
            .join(",");
          const synergySig = Object.entries(boot.ctx.synergyLevels().toState())
            .sort()
            .map(([k, v]) => `${k}:${v}`)
            .join(",");
          return (
            [...boot.state.discovered()].sort().join(",") +
            `#${countSig}#${boot.ctx.chapter().bestChapter}#${boot.state.codexSeenChapter()}#${synergySig}#${boot.ctx.upgradeInv().universalUpgradeStone}#${view.currentSynergyMultiplier()}`
            + `#${boot.ctx.spaceship().ownedIds().join(",")}`
          );
        };
        return viewTabHandle(view, refresh, codexSignature);
      },
    });
  },
};
